import { useEffect, useState } from "react";
import { AlertTriangle, ClipboardList, Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TemplateEditorDialog } from "@/components/TemplateEditorDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function TemplatesPage() {
  const { user } = useAuth();
  const canWrite = ["owner", "admin"].includes(user?.role);
  const [templates, setTemplates] = useState([]);
  const [editor, setEditor] = useState({ open: false, template: null, mode: "new" });
  const [del, setDel] = useState(null); // { template, usage, replacementId, deleting }

  const load = () => api.get("/templates").then((response) => setTemplates(response.data));
  useEffect(() => { load(); }, []);

  const openEditor = (mode, template = null) => setEditor({ open: true, template, mode });

  async function startDelete(template) {
    try {
      const usage = (await api.get(`/templates/${template.id}/usage`)).data;
      setDel({ template, usage, replacementId: "", deleting: false });
    } catch (error) {
      toast.error(formatApiError(error));
    }
  }

  async function confirmDelete() {
    if (!del) return;
    const { template, usage, replacementId } = del;
    if (usage.count > 0 && !replacementId) { toast.error("Choose a replacement template first."); return; }
    setDel((prev) => ({ ...prev, deleting: true }));
    try {
      const query = usage.count > 0 ? `?replacement_template_id=${replacementId}` : "";
      await api.delete(`/templates/${template.id}${query}`);
      toast.success(usage.count > 0 ? `Template deleted. ${usage.count} insured${usage.count === 1 ? "" : "s"} reassigned and re-verified.` : "Template deleted.");
      setDel(null);
      load();
    } catch (error) {
      toast.error(formatApiError(error));
      setDel((prev) => ({ ...prev, deleting: false }));
    }
  }

  const replacementOptions = del ? templates.filter((t) => t.id !== del.template.id) : [];

  return (
    <section data-testid="templates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Requirement library</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">Requirement templates</h1>
          <p className="mt-1 text-sm text-slate-600">Set the coverage rules each insured group must meet. {canWrite ? "Editing a template re-verifies every insured that uses it." : "You have read-only access to these templates."}</p>
        </div>
        {canWrite && <Button data-testid="create-template-button" onClick={() => openEditor("new")}><Plus size={16} />New template</Button>}
      </div>

      <div className="mt-7 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {templates.map((template) => (
          <Card className="border-slate-200 shadow-none" data-testid={`template-card-${template.id}`} key={template.id}>
            <CardContent className="flex h-full flex-col p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-700"><ClipboardList size={19} /></div>
                {template.isDefault && <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700" data-testid={`default-template-badge-${template.id}`}>Default</span>}
              </div>
              <h2 className="mt-5 text-lg font-semibold text-slate-900">{template.name}</h2>
              <p className="mt-2 text-sm text-slate-600">Version {template.version} · {template.coverages.length} required coverage {template.coverages.length === 1 ? "type" : "types"}</p>
              <div className="mt-4 border-t border-slate-100 pt-4 text-xs text-slate-500">Certificate holder: {template.certificateHolder.expectedName || "Not specified"}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button className="gap-1.5" data-testid={`template-edit-${template.id}`} onClick={() => openEditor(canWrite ? "edit" : "view", template)} size="sm" variant="outline"><Pencil size={14} />{canWrite ? "Edit" : "View"}</Button>
                {canWrite && <Button className="gap-1.5" data-testid={`template-clone-${template.id}`} onClick={() => openEditor("clone", template)} size="sm" variant="outline"><Copy size={14} />Clone</Button>}
                {canWrite && !template.isDefault && <Button className="gap-1.5 text-red-600 hover:text-red-700" data-testid={`template-delete-${template.id}`} onClick={() => startDelete(template)} size="sm" variant="ghost"><Trash2 size={14} />Delete</Button>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!templates.length && <div className="mt-12 grid min-h-64 place-items-center rounded-lg border border-dashed border-slate-300 bg-white text-center"><div><ClipboardList className="mx-auto text-slate-300" size={44} /><h2 className="mt-3 font-semibold">No templates yet</h2></div></div>}

      <TemplateEditorDialog canWrite={canWrite && editor.mode !== "view"} mode={editor.mode} onOpenChange={(open) => setEditor((prev) => ({ ...prev, open }))} onSaved={load} open={editor.open} template={editor.template} />

      <Dialog onOpenChange={(open) => !open && setDel(null)} open={!!del}>
        <DialogContent data-testid="template-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete “{del?.template?.name}”?</DialogTitle>
            <DialogDescription>{del?.usage?.count > 0 ? "This template is being used to compare against the following insureds. Choose a replacement — they’ll be reassigned and re-verified against it before this template is deleted." : "This template isn’t assigned to any insureds. This action can’t be undone."}</DialogDescription>
          </DialogHeader>
          {del?.usage?.count > 0 && (
            <div className="space-y-4">
              <div className="max-h-40 overflow-y-auto rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="template-usage-list">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-800"><AlertTriangle size={13} />{del.usage.count} insured{del.usage.count === 1 ? "" : "s"} affected</p>
                <ul className="space-y-1 text-sm text-amber-900">{del.usage.insureds.map((ins) => <li data-testid={`usage-insured-${ins.id}`} key={ins.id}>{ins.name}</li>)}</ul>
              </div>
              <div>
                <p className="mb-1.5 text-sm font-medium text-slate-700">Reassign these insureds to</p>
                <Select onValueChange={(v) => setDel((prev) => ({ ...prev, replacementId: v }))} value={del.replacementId}>
                  <SelectTrigger data-testid="replacement-template-select"><SelectValue placeholder="Choose a replacement template" /></SelectTrigger>
                  <SelectContent>{replacementOptions.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.isDefault ? " (Default)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDel(null)} type="button" variant="outline" data-testid="template-delete-cancel">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="template-delete-confirm" disabled={del?.deleting || (del?.usage?.count > 0 && !del?.replacementId)} onClick={confirmDelete}>{del?.deleting ? "Deleting…" : "Delete template"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
